// Modelo130Page.tsx — standalone indexable "Calculadora Modelo 130" page (F15b, plan §4 F15).
//
// Lives OUTSIDE the SPA shell (no Firebase/Sentry/contexts) — see the F15a pattern notes in
// CalculadoraIrpf2025Page.tsx. Every figure comes from computeModelo130Estimate →
// computeDirectEstimationSimplified (engine2025). Never hardcode a fiscal number here: even
// the difficult-justification copy reads the engine param (getDifficultJustificationInfo).

/* eslint-disable gestorpro/no-spanish-jsx --
   Documented F15a/F15b decision (plan §4): standalone Spanish-market SEO page rendered
   OUTSIDE the app shell (no i18n runtime); copy is direct Spanish by design. */

import React, { useMemo, useState } from 'react';
import { ID_DICTIONARY } from '../../constants/id-dictionary';
import { CalculatorShell } from '../shared/CalculatorShell';
import { computeModelo130Estimate, getDifficultJustificationInfo, MODELO_130_PAYMENT_RATE } from './calc';

const AMOUNT_MIN = 0;
const AMOUNT_MAX = 10000000;

const formatEuros = (value: number): string =>
    `${value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const clampAmount = (value: number): number =>
    Number.isFinite(value) ? Math.min(AMOUNT_MAX, Math.max(AMOUNT_MIN, Math.round(value))) : 0;

export const Modelo130Page: React.FC = () => {
    const [income, setIncome] = useState(12000);
    const [expenses, setExpenses] = useState(4000);

    const estimate = useMemo(() => computeModelo130Estimate(income, expenses), [income, expenses]);
    const dji = useMemo(() => getDifficultJustificationInfo(), []);
    const paymentRatePct = MODELO_130_PAYMENT_RATE * 100;

    return (
        <CalculatorShell
            rootId={ID_DICTIONARY.CALCULATORS.M130_ROOT}
            heading="Calculadora Modelo 130 (2025)"
            intro="Estima el pago fraccionado de IRPF del trimestre para autónomos en estimación directa simplificada, con el mismo motor fiscal verificado que usa GestorPro. Gratis, sin registro y calculado íntegramente en tu navegador."
            disclaimerId={ID_DICTIONARY.CALCULATORS.M130_DISCLAIMER}
            disclaimerBody="Estimación orientativa y simplificada sobre los datos que introduces: el modelo 130 oficial se autoliquida con tus cifras reales y el importe final puede diferir. No constituye asesoramiento fiscal."
            ctaId={ID_DICTIONARY.CALCULATORS.M130_CTA_APP}
            ctaHeading="Deja de calcularlo a mano"
            ctaBody="GestorPro importa tus extractos bancarios y estima tus modelos 130 y 303 automáticamente a partir de tus movimientos reales, trimestre a trimestre, con este mismo motor fiscal."
            currentPath="/calculadoras/modelo-130/"
            scopeNote="Cálculo según la normativa estatal (AEAT) para estimación directa simplificada. Si tributas en País Vasco o Navarra (régimen foral), tu hacienda foral tiene modelos y reglas propios."
        >
            {/* Form card */}
            <section className="bg-card border border-border rounded-xl shadow-e1 p-5 sm:p-6 flex flex-col gap-5">
                <div className="flex items-center gap-2 border-b border-border pb-3">
                    <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    <h2 className="text-xs font-extrabold text-muted-foreground uppercase tracking-wider">
                        Tus datos del trimestre
                    </h2>
                </div>

                <div className="flex items-center justify-between gap-3">
                    <label htmlFor={ID_DICTIONARY.CALCULATORS.M130_INCOME_INPUT} className="text-xs font-bold text-muted-foreground">
                        Ingresos del periodo
                    </label>
                    <div className="flex items-center gap-1">
                        <input
                            id={ID_DICTIONARY.CALCULATORS.M130_INCOME_INPUT}
                            type="number"
                            inputMode="numeric"
                            min={AMOUNT_MIN}
                            max={AMOUNT_MAX}
                            step={100}
                            value={income}
                            onChange={(e) => setIncome(clampAmount(Number(e.target.value)))}
                            className="w-32 bg-muted border-2 border-border rounded-xl px-2.5 py-1.5 text-sm font-extrabold text-foreground text-right tabular-nums focus:outline-none focus:border-primary transition-colors"
                        />
                        <span className="text-sm font-extrabold text-foreground">€</span>
                    </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                    <label htmlFor={ID_DICTIONARY.CALCULATORS.M130_EXPENSES_INPUT} className="text-xs font-bold text-muted-foreground">
                        Gastos deducibles del periodo
                    </label>
                    <div className="flex items-center gap-1">
                        <input
                            id={ID_DICTIONARY.CALCULATORS.M130_EXPENSES_INPUT}
                            type="number"
                            inputMode="numeric"
                            min={AMOUNT_MIN}
                            max={AMOUNT_MAX}
                            step={100}
                            value={expenses}
                            onChange={(e) => setExpenses(clampAmount(Number(e.target.value)))}
                            className="w-32 bg-muted border-2 border-border rounded-xl px-2.5 py-1.5 text-sm font-extrabold text-foreground text-right tabular-nums focus:outline-none focus:border-primary transition-colors"
                        />
                        <span className="text-sm font-extrabold text-foreground">€</span>
                    </div>
                </div>

                <p className="text-[11px] text-muted-foreground leading-relaxed bg-muted/40 border border-border p-3 rounded-xl">
                    {`Supuestos del cálculo: estimación directa simplificada, con gastos de difícil
                    justificación del ${dji.pct.toLocaleString('es-ES')} % (tope
                    ${dji.maxAmount.toLocaleString('es-ES')} €) aplicados automáticamente por el motor,
                    y pago fraccionado del ${paymentRatePct.toLocaleString('es-ES')} % del rendimiento
                    neto. La autoliquidación oficial es acumulada en el año y descuenta pagos y
                    retenciones anteriores, que esta estimación simple no modela.`}
                </p>
            </section>

            {/* Result card */}
            <section
                id={ID_DICTIONARY.CALCULATORS.M130_RESULT_CARD}
                className="bg-card border border-border rounded-xl shadow-e1 p-5 sm:p-6 flex flex-col gap-4"
                aria-live="polite"
            >
                <h2 className="text-xs font-extrabold text-muted-foreground uppercase tracking-wider">
                    Resultado estimado
                </h2>
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="type-label block">{`Pago fraccionado estimado (${paymentRatePct.toLocaleString('es-ES')} %)`}</span>
                        <span className="type-kpi-hero text-foreground">{formatEuros(estimate.payment)}</span>
                    </div>
                    <div className="text-right">
                        <span className="type-label block">Rendimiento neto estimado</span>
                        <span className="type-kpi text-foreground">{formatEuros(estimate.netYield)}</span>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-3 tabular-nums">
                    {`Gastos de difícil justificación aplicados por el motor: ${formatEuros(estimate.difficultJustification)}.`}
                </p>
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    El cálculo se ejecuta en tu navegador: tus cifras no se envían a ningún servidor.
                </p>
            </section>
        </CalculatorShell>
    );
};
